import { createFileRoute, Link } from "@tanstack/react-router";
import { FaqSection } from "@/components/FaqSection";
import { SITE_URL, hreflangLinks, faqPageSchema } from "@/lib/seo";

const PATH = "/pakistan/market-hours";
const TITLE = "Why Is Gold Market Closed Today in Pakistan? — Sarafa Bazaar Timings";
const DESC = "Why is gold market closed today in Pakistan? Karachi, Lahore, Islamabad sarafa bazaar timings, weekly holidays, Friday hours, Eid closures & international XAU/USD trading hours.";

const FAQS = [
  { q: "Why is gold market closed today in Pakistan?", a: "Pakistan sarafa bazaars are typically closed on Sunday and half-day on Friday for Jumma prayers. Additional closures happen on Eid-ul-Fitr, Eid-ul-Adha, Muharram 9–10, 12 Rabi-ul-Awwal, 14 August (Independence Day), 25 December (Quaid Day) and any government-declared holiday." },
  { q: "What time does the gold market open in Karachi?", a: "Karachi sarafa bazaar (Saddar aur Bolton Market) typically opens at 10:00 AM PKT and closes around 6:00–7:00 PM. Friday timings are 10:00 AM to 12:00 PM before Jumma, and 3:00 PM to 6:00 PM after." },
  { q: "Is the international gold market open on Sunday?", a: "The international spot market (XAU/USD) opens Sunday 5 PM Eastern Time and closes Friday 5 PM Eastern Time — 24/5 trading. When Pakistan sarafa bazaars are closed, international spot may still move, and our rate widget continues updating." },
  { q: "What are Lahore gold market timings?", a: "Lahore sarafa bazaar (Anarkali aur Data Ganj Bakhsh area) opens 10:00 AM to 7:00 PM Monday–Saturday. Sunday closed. Friday same split as Karachi." },
  { q: "Does gold rate change when Pakistan market is closed?", a: "Yes — international XAU/USD trades Sunday evening to Friday evening, so the international spot component keeps moving even when local sarafa bazaars are closed. Local retail rates freeze until the bazaar reopens." },
  { q: "Where can I check live gold rate when market is closed?", a: "Our live gold rate in Pakistan page keeps showing the international spot-derived rate 24/7, refreshing every 10 minutes. It's the closest indicator of where local rates will open the next trading day." },
];

export const Route = createFileRoute("/pakistan/market-hours")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: `${SITE_URL}${PATH}` },
      { property: "og:type", content: "article" },
      { property: "og:locale", content: "en_PK" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${PATH}` }, ...hreflangLinks(PATH)],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqPageSchema(FAQS)) }],
  }),
  component: MarketHoursPage,
});

function MarketHoursPage() {
  return (
    <main className="mx-auto max-w-4xl px-4 py-8">
      <nav className="text-xs text-muted-foreground mb-4">
        <Link to="/pakistan" className="hover:text-primary">Pakistan Gold Rate</Link> / Market Hours
      </nav>
      <h1 className="text-3xl md:text-4xl font-bold">Why Is Gold Market Closed Today? — Pakistan Sarafa Bazaar Timings</h1>
      <p className="mt-4 text-foreground/85 leading-relaxed">
        Agar aap check kar rahe hain <strong>why gold market is closed today in
        Pakistan</strong>, ye page batayega har major sarafa bazaar (Karachi,
        Lahore, Islamabad, Rawalpindi) ke opening hours, weekly holidays, aur
        Eid + national holiday closures. For today's live rate even when the
        market is closed, jump to our{" "}
        <Link to="/pakistan" className="text-primary underline">live gold rate in Pakistan</Link>.
      </p>

      <section className="mt-8 overflow-x-auto rounded-2xl border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-3">Bazaar</th>
              <th className="text-left p-3">Mon–Thu</th>
              <th className="text-left p-3">Friday</th>
              <th className="text-left p-3">Sat</th>
              <th className="text-left p-3">Sun</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["Karachi (Saddar/Bolton)", "10 AM – 7 PM", "10–12, 3–6", "10 AM – 6 PM", "Closed"],
              ["Lahore (Anarkali/Data)", "10 AM – 7 PM", "10–12, 3–6", "10 AM – 6 PM", "Closed"],
              ["Islamabad (Blue Area)", "10 AM – 6 PM", "10–12, 3–6", "10 AM – 5 PM", "Closed"],
              ["Rawalpindi (Raja Bazaar)", "10 AM – 7 PM", "10–12, 3–6", "10 AM – 6 PM", "Closed"],
              ["International XAU/USD", "24 hrs", "24 hrs (till 5 PM ET)", "Closed", "Opens 5 PM ET"],
            ].map((r) => (
              <tr key={r[0]} className="border-t border-border">
                {r.map((c, i) => <td key={i} className="p-3">{c}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="mt-6 rounded-2xl border border-border bg-card p-6">
        <h2 className="text-xl font-bold mb-3">Fixed annual closures</h2>
        <ul className="list-disc ml-6 text-sm space-y-1">
          <li>Eid-ul-Fitr (3 days)</li>
          <li>Eid-ul-Adha (3 days)</li>
          <li>Muharram 9–10 (Ashura)</li>
          <li>12 Rabi-ul-Awwal (Eid Milad-un-Nabi)</li>
          <li>23 March (Pakistan Day)</li>
          <li>1 May (Labour Day)</li>
          <li>14 August (Independence Day)</li>
          <li>25 December (Quaid-e-Azam Day)</li>
        </ul>
      </section>

      <FaqSection faqs={FAQS} title="Pakistan gold market timings — FAQs" />
    </main>
  );
}